import type { GenerationStatsDayRow, GenerationStatsGroup, GenerationStatsUnits } from '@mpr/shared';

/**
 * The usage and cost screen's logic that has no screen of its own: how money
 * is written, when a figure is "not priced" rather than a number, which
 * bucket size suits a range, and the heights of the bars. Kept apart from the
 * component so each rule can be tested without rendering anything.
 */

/** Dollars, never rounded to a misleading zero: cents above a dollar, four places below. */
export function formatUsd(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1) {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  // 0.05 stays "0.05", 0.0003 stays "0.0003": trailing zeros trimmed, but never fewer than two places.
  const fixed = value.toFixed(4).replace(/0+$/, '');
  const [whole = '0', fraction = ''] = fixed.split('.');
  return `$${whole}.${fraction.padEnd(2, '0')}`;
}

export type CostDisplay =
  /** Nothing finished here, so there is no figure to give. */
  | { kind: 'none' }
  /** Everything finished is without a price: say so instead of printing 0. */
  | { kind: 'unpriced' }
  /** A figure, with some finished jobs left out of it. */
  | { kind: 'partial'; costUsd: number; unknown: number }
  | { kind: 'priced'; costUsd: number };

/** How a row's cost is shown. Zero is only ever printed when something was priced. */
export function costDisplay(row: { costUsd: number; done: number; unknownPriceJobs: number }): CostDisplay {
  if (row.done === 0) return { kind: 'none' };
  if (row.unknownPriceJobs >= row.done) return { kind: 'unpriced' };
  if (row.unknownPriceJobs > 0) return { kind: 'partial', costUsd: row.costUsd, unknown: row.unknownPriceJobs };
  return { kind: 'priced', costUsd: row.costUsd };
}

/** A project's or job's own cost: a number, or "not priced" when it has none. */
export function jobCostDisplay(cost: { costUsd?: number | null; priceKnown?: boolean }): CostDisplay {
  if (cost.costUsd === null || cost.costUsd === undefined) return { kind: 'unpriced' };
  return cost.priceKnown === false
    ? { kind: 'partial', costUsd: cost.costUsd, unknown: 1 }
    : { kind: 'priced', costUsd: cost.costUsd };
}

/**
 * What to say about the cost of one finished thing (a result, a project), or
 * nothing at all when the server did not say (an older response) or there is
 * nothing finished yet to price.
 */
export function costLine(cost: { costUsd?: number | null; priceKnown?: boolean }): CostDisplay | null {
  if (cost.costUsd === undefined && cost.priceKnown === undefined) return null;
  if (cost.costUsd === null || cost.costUsd === undefined) return cost.priceKnown === false ? { kind: 'unpriced' } : null;
  return cost.priceKnown === false
    ? { kind: 'partial', costUsd: cost.costUsd, unknown: 1 }
    : { kind: 'priced', costUsd: cost.costUsd };
}

export const RANGE_PRESETS = [7, 30, 90] as const;

const isoDay = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** The last `days` days ending today, as the date strings the API takes. */
export function rangeForDays(days: number, today: Date = new Date()): { from: string; to: string } {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (days - 1));
  return { from: isoDay(start), to: isoDay(today) };
}

/** Whole days a range spans, both ends included; at least 1. */
export function daysInRange(from: string, to: string): number {
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
  return Number.isFinite(ms) ? Math.max(1, Math.round(ms / 86_400_000) + 1) : 1;
}

/** A bar per day for a month, per week for a quarter, per month beyond: always readable on a phone. */
export function groupFor(days: number): GenerationStatsGroup {
  if (days <= 31) return 'day';
  if (days <= 120) return 'week';
  return 'month';
}

export interface Bar {
  day: string;
  costUsd: number;
  jobs: number;
  unknownPriceJobs: number;
  /** 0 to 1 of the tallest bar; a bar with any cost is never thinner than a sliver. */
  ratio: number;
}

const MIN_VISIBLE_RATIO = 0.03;

export function buildBars(rows: GenerationStatsDayRow[]): { bars: Bar[]; max: number } {
  const max = rows.reduce((top, row) => Math.max(top, row.costUsd), 0);
  const bars = rows.map((row) => ({
    day: row.day,
    costUsd: row.costUsd,
    jobs: row.jobs,
    unknownPriceJobs: row.unknownPriceJobs,
    ratio: max > 0 && row.costUsd > 0 ? Math.max(row.costUsd / max, MIN_VISIBLE_RATIO) : 0,
  }));
  return { bars, max };
}

export type UnitPart =
  | { kind: 'seconds' | 'images' | 'chars'; value: number }
  | { kind: 'tokens'; input: number; output: number };

/** What was consumed, as the non-zero parts only, in a fixed order. */
export function unitParts(units: GenerationStatsUnits): UnitPart[] {
  const parts: UnitPart[] = [];
  if (units.seconds > 0) parts.push({ kind: 'seconds', value: Math.round(units.seconds) });
  if (units.images > 0) parts.push({ kind: 'images', value: units.images });
  if (units.chars > 0) parts.push({ kind: 'chars', value: units.chars });
  if (units.inputTokens > 0 || units.outputTokens > 0) {
    parts.push({ kind: 'tokens', input: units.inputTokens, output: units.outputTokens });
  }
  return parts;
}
